import { push } from '@lagunovsky/redux-react-router'
import { Card, CardContent, Grid, Stack } from '@mui/material'
import HomeAction from 'home/actions/HomeAction'
import { omit } from 'lodash'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import i18n from 'simple-react-i18n'
import ToastrAction from 'toastr/actions/ToastrAction'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import { SENSOR_ID } from 'materiel/constants/MaterielConstants'
import { H_MAT_SENSOR } from '../../../../account/constants/AccessRulesConstants'
import FilePanel from '../../../../components/file/FilePanel'
import SmallPicturePanel from '../../../../components/picture/SmallPicturePanel'
import UpdatePanel from '../../../../components/updatePanel/UpdatePanel'
import { componentHasHabilitations } from '../../../../utils/HabilitationUtil'
import { updateMaterialize } from '../../../../utils/MaterializeUtils'
import { getUser } from '../../../../utils/SettingUtils'
import { isSaveBlocked } from '../../../utils/MaterielUtils'
import AdministratorPanel from '../../administrator/AdministratorPanel'
import AssigmentChartPanel from '../../chart/AssigmentChartPanel'
import LastSituationMarkerPanel from '../../situation/LastSituationMarkerPanel'
import SituationPanel from '../../situation/SituationPanel'
import PowerSupplyAction from '../../powerSupply/actions/PowerSupplyAction'
import SensorAction from '../actions/SensorAction'
import SensorPanel from './SensorPanel'

const SensorApp = () => {
    const {
        sensor: storeSensor,
        sensorTypes,
        sensorSituations,
        powerSupplyTypes,
        sensorPictures,
        sensorFiles,
        sensors,
        sensorsLastSituations,
        materielSettingRules,
    } = useSelector(store => ({
        sensor: store.SensorReducer.sensor,
        sensorTypes: store.SensorReducer.sensorTypes,
        sensorSituations: store.SensorReducer.sensorSituations,
        powerSupplyTypes: store.PowerSupplyReducer.powerSupplyTypes,
        sensorPictures: store.SensorReducer.sensorPictures,
        sensorFiles: store.SensorReducer.sensorFiles,
        sensors: store.SensorReducer.sensors,
        sensorsLastSituations: store.SensorReducer.sensorsLastSituations,
        materielSettingRules: store.MaterielReducer.materielSettingRules,
    }))

    const dispatch = useDispatch()
    const params = useParams()

    const [sensor, setSensor] = useState({})
    const [isEditMode, setIsEditMode] = useState(false)

    const fetchSensor = id => dispatch(SensorAction.fetchSensor(id)).then(json => {
        if (json.id) {
            dispatch(SensorAction.fetchSensorSituations(json.id))
            dispatch(SensorAction.fetchSensorPictures(json.id))
            dispatch(SensorAction.fetchSensorFiles(json.serialNumber))
            setSensor({ ...json })
        }
    })

    const changeAdministrator = administrator => setSensor(prev => ({ ...prev, administrator }))

    const changeStateAttribute = changes => setSensor(prev => ({ ...prev, ...changes }))

    const deleteSituation = id => dispatch(SensorAction.deleteSensorSituation(id, params.id))

    const filterConsultant = actions => getUser().consultant === '1'
        ? omit(actions, ['save', 'edit', 'delete', 'replace'])
        : actions

    useActions(() => {
        updateMaterialize()
        if (params.id === 'new' && !storeSensor.id) {
            return filterConsultant({
                save: () => {
                    const rules = materielSettingRules.filter(({ idMaterielType }) => idMaterielType === SENSOR_ID)
                    if (isSaveBlocked(sensors, sensor, rules, 'sensorType')) return

                    dispatch(SensorAction.createSensor(sensor)).then(id => {
                        if (id) {
                            const situation = {
                                idSensor: id,
                                situationDate: sensor.purchaseDate,
                                statusCode: 2,
                            }
                            dispatch(SensorAction.saveSensorSituation(situation))
                        }
                    })
                },
                cancel: () => {
                    dispatch(push('/materiel/sensor'))
                },
            })
        }
        if (isEditMode) {
            return filterConsultant({
                save: () => {
                    const rules = materielSettingRules.filter(({ idMaterielType }) => idMaterielType === SENSOR_ID)
                    if (isSaveBlocked(sensors, sensor, rules, 'sensorType')) return

                    dispatch(SensorAction.updateSensor(sensor)).then(success => success && setIsEditMode(false))
                },
                cancel: () => {
                    setSensor(storeSensor)
                    setIsEditMode(false)
                },
            })
        }
        return filterConsultant({
            edit: () => {
                setIsEditMode(true)
            },
            delete: () => {
                dispatch(SensorAction.deleteSensor(params.id))
            },
            replace: () => {
                dispatch(ToastrAction.info(i18n.inDeveloppmentFunctionnality))
            },
        })
    }, [isEditMode, sensor, storeSensor, sensors, materielSettingRules])

    useTitle(() => {
        const { reference = '', serialNumber = '', sensorType, id } = storeSensor
        const title = reference || serialNumber || id
        const type = (sensorTypes.find(t => t.code === parseInt(sensorType)) || {}).label || i18n.notDefined
        return ([
            {
                title: i18n.materiels,
                href: 'materiel',
            },
            {
                title: i18n.sensors,
                href: 'materiel/sensor',
            },
            {
                title: type,
                href: `materiel/sensor/${params.id}`,
            },
            {
                title,
                href: `materiel/sensor/${params.id}`,
            },
        ])
    }, [storeSensor, sensorTypes])

    useEffect(() => {
        if (!componentHasHabilitations(H_MAT_SENSOR)) { // A modifier quand react-router sera à jour
            dispatch(push('/unauthorized'))
            return undefined
        }
        if (!sensors.length) {
            dispatch(SensorAction.fetchSensors(true))
        }
        if (!sensorTypes.length) {
            dispatch(SensorAction.fetchSensorTypes())
        }
        if (!powerSupplyTypes.length) {
            dispatch(PowerSupplyAction.fetchPowerSupplyTypes())
        }
        dispatch(HomeAction.setHelpLink('materiel', ''))
        return () => dispatch(SensorAction.resetSensor())
    }, [])

    useEffect(() => {
        if (params.id !== 'new') {
            fetchSensor(params.id)
            setIsEditMode(false)
        } else {
            setIsEditMode(true)
        }
    }, [params.id])

    const getMaterielNumber = ({ serialNumber, reference }) => serialNumber || reference || ''

    const getSensorLabel = s => {
        const number = getMaterielNumber(s)
        if (s.sensorType) {
            const type = sensorTypes.find(t => t.id === s.sensorType)
            if (type) {
                return `${type.label} - ${number}`
            }
        }
        return number
    }

    const getSensorsFormat = () => sensors
        .filter(({ isEquipment }) => !isEquipment)
        .map(s => ({
            id: s.id,
            label: getSensorLabel(s),
        }))

    const disabled = { active: isEditMode, disabled: !isEditMode }

    return (
        <Card>
            <CardContent>
                <Grid container size={12} spacing={2}>
                    <Grid container size={9}>
                        <LastSituationMarkerPanel materielSituation={sensorSituations} />
                        <Grid container size={'grow'}>
                            <Grid size={12}>
                                <SensorPanel
                                    sensor={sensor}
                                    onChange={changeStateAttribute}
                                    {...disabled}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid size={3}>
                        <Stack spacing={1}>
                            <UpdatePanel
                                updateDate={storeSensor.updateDate}
                                updateLogin={storeSensor.updateLogin}
                                jobExecId={storeSensor.jobExecutionId}
                            />
                            <Card>
                                <CardContent>
                                    <AdministratorPanel
                                        onChange={changeAdministrator}
                                        selectedValue={sensor.administrator}
                                        isActive={!storeSensor.isEquipment && isEditMode}
                                    />
                                </CardContent>
                            </Card>
                            <AssigmentChartPanel materielSituation={sensorSituations} />
                            <SmallPicturePanel pictures={sensorPictures} />
                            <FilePanel files={sensorFiles} />
                        </Stack>
                    </Grid>
                    {
                        !storeSensor.isEquipment && (
                            <Grid size={12}>
                                <SituationPanel
                                    materielSituation={sensorSituations}
                                    active={isEditMode}
                                    idMateriel={storeSensor.id}
                                    serialNumber={storeSensor.serialNumber}
                                    purchaseDate={storeSensor.purchaseDate}
                                    idKey={'idSensor'}
                                    lastSituations={sensorsLastSituations}
                                    materials={getSensorsFormat()}
                                    saveFunction={situation => dispatch(SensorAction.saveSensorSituation(situation))}
                                    deleteFunction={deleteSituation}
                                />
                            </Grid>
                        )
                    }
                </Grid>
            </CardContent>
        </Card>
    )
}

export default SensorApp
